import { useCurrencyContext } from '@apeiron/currency'
import { OrbitalTrackRentalPopupType } from '@apeiron/orbital'
import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import DateLinearProgress from '@src/components/share/generic/DateLinearProgress'
import { OrbitalTrackDetachDetail } from '@src/types/orbitalTrack'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  background: #171719;
  border-bottom: 2px dashed rgba(128, 128, 126, 0.5);
  padding: 20px;
`

const DialogTitle = styled(Typography)`
  font-weight: 400;
  margin-bottom: 20px;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 20px;
    line-height: 24px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 14px;
    line-height: 20px;
  }
`

const Progress = styled(DateLinearProgress)`
  flex: 1;
`

const RefundTable = styled(Table)`
  margin-top: 20px;
  width: 100%;
`

const HeaderRowContainer = styled(TableRow)`
  background: black;
`

const HeaderCell = styled(TableCell)`
  border-bottom: unset;
  color: white;
  text-align: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 16px;
    font-weight: 300;
    line-height: 18px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 20px;
  }
`

const RowContainer = styled(TableRow)`
  background: linear-gradient(99.04deg, #1e1e1e -0.18%, #000000 78.14%);
  border-bottom: 1px solid #484848;
  height: 70px;
`

const LastRowContainer = styled(RowContainer)`
  border-bottom: 1px solid #27272a;
`

const RefundCell = styled(TableCell)`
  border-bottom: unset;
  color: white;
  font-weight: 300;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 20px;
    line-height: 24px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    line-height: 20px;
  }
`

const LabelCell = styled(RefundCell)`
  width: 65%;
`

const ProgressCell = styled(RefundCell)`
  min-width: 120px;
  width: 35%;
`

const ProgressBox = styled(Box)`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  width: 100%;
`

const PercentageCell = styled(RefundCell)`
  text-align: center;
  width: 15%;
`

const AmountCell = styled(RefundCell)`
  text-align: center;
  width: 20%;
`

const OrbitingPlanetSummary: FC<Props> = props => {
  const {
    className,
    popupType = OrbitalTrackRentalPopupType.Renew,
    rentalDetail
  } = props

  const { getApeironPrice, getCurrency } = useCurrencyContext()

  const { t } = useTranslation()

  const extractRefundText = useCallback(
    (
      rentalDetail: OrbitalTrackDetachDetail | undefined,
      field:
        | 'starOwnerRentalFeeAmount'
        | 'planetOwnerRefundAmount'
        | 'rentalFeeAfterPlatform'
        | 'platformFee'
        | 'rentalFee'
    ): string => {
      if (R.isNil(rentalDetail)) {
        return '-'
      }

      const currency = getCurrency(rentalDetail.currencyType)

      return `${getApeironPrice(
        rentalDetail.currencyType,
        rentalDetail[field]
      )} ${currency?.name}`
    },
    [getApeironPrice]
  )

  const starOwnerRefund = useMemo((): string => {
    return extractRefundText(rentalDetail, 'starOwnerRentalFeeAmount')
  }, [extractRefundText, rentalDetail])

  const planetOwnerRefund = useMemo((): string => {
    return extractRefundText(rentalDetail, 'planetOwnerRefundAmount')
  }, [extractRefundText, rentalDetail])

  const platformFee = useMemo((): string => {
    return extractRefundText(rentalDetail, 'platformFee')
  }, [extractRefundText, rentalDetail])

  const totalRentalFee = useMemo((): string => {
    return extractRefundText(rentalDetail, 'rentalFee')
  }, [extractRefundText, rentalDetail])

  const dialogTitle = () => {
    switch (popupType) {
      case OrbitalTrackRentalPopupType.Planet_Owner:
        return t(
          'godiverse.orbital_track.rental_popup.summary.end_title_planet'
        )
      case OrbitalTrackRentalPopupType.Star_Owner:
        return t('godiverse.orbital_track.rental_popup.summary.end_title_star')
      case OrbitalTrackRentalPopupType.Renew:
        return t('godiverse.orbital_track.rental_popup.summary.renew_title')
    }
  }

  return (
    <MainContainer className={className}>
      {!R.isNil(rentalDetail) && (
        <>
          <DialogTitle>{dialogTitle()}</DialogTitle>
          <RefundTable>
            <TableBody>
              <LastRowContainer>
                <LabelCell>
                  {t(
                    'godiverse.orbital_track.rental_popup.summary.rental_complete'
                  )}
                </LabelCell>
                <ProgressCell>
                  <ProgressBox>
                    <Progress
                      startAt={new Date(rentalDetail?.attachTime || '')}
                      now={new Date()}
                      endAt={new Date(rentalDetail?.contractEndTime || '')}
                    />
                    <Typography>
                      {rentalDetail.rentalTimePercent / 100}%
                    </Typography>
                  </ProgressBox>
                </ProgressCell>
              </LastRowContainer>
            </TableBody>
          </RefundTable>
          <RefundTable>
            <TableHead>
              <HeaderRowContainer>
                <HeaderCell />
                <HeaderCell>
                  {t('godiverse.orbital_track.rental_popup.summary.user_split')}
                </HeaderCell>
                <HeaderCell>
                  {t('godiverse.orbital_track.rental_popup.summary.amount')}
                </HeaderCell>
              </HeaderRowContainer>
            </TableHead>
            <TableBody>
              <RowContainer>
                <LabelCell>
                  {t('godiverse.orbital_track.rental_popup.summary.star_owner')}
                </LabelCell>
                <PercentageCell>
                  {rentalDetail?.starOwnerRentalFeePercent / 100}%
                </PercentageCell>
                <AmountCell>{starOwnerRefund}</AmountCell>
              </RowContainer>
              <RowContainer>
                <LabelCell>
                  {t(
                    'godiverse.orbital_track.rental_popup.summary.planet_owner'
                  )}
                </LabelCell>
                <PercentageCell>
                  {rentalDetail?.planetOwnerRefundPercent / 100}%
                </PercentageCell>
                <AmountCell>{planetOwnerRefund}</AmountCell>
              </RowContainer>
              <RowContainer>
                <LabelCell>
                  {t(
                    'godiverse.orbital_track.rental_popup.summary.platform_fee'
                  )}
                </LabelCell>
                <PercentageCell />
                <AmountCell>{platformFee}</AmountCell>
              </RowContainer>
              <LastRowContainer>
                <LabelCell>
                  {t('godiverse.orbital_track.rental_popup.summary.total')}
                </LabelCell>
                <PercentageCell />
                <AmountCell>{totalRentalFee}</AmountCell>
              </LastRowContainer>
            </TableBody>
          </RefundTable>
        </>
      )}
    </MainContainer>
  )
}

type Props = {
  className?: string
  popupType?: OrbitalTrackRentalPopupType
  rentalDetail?: OrbitalTrackDetachDetail
}

export default OrbitingPlanetSummary
